import React, { useState } from "react";
import { ChevronDownIcon, LightbulbIcon } from "lucide-react";
import BookIcon from "src/assets/images/book.svg";
import SwitchIcon from "src/assets/images/switch.svg";
import { Switch } from "@headlessui/react";

import { useAppDispatch } from "src/hooks/redux";
import jsCookie from "js-cookie";
import { useSearchParams } from "react-router-dom";
import { updateCleanup } from "../action";

interface CleanupOption {
    name: string;
    description: string;
    default: 0 | 1;
    enabled?: boolean;
}

interface ProposedCleanupProps {
    json_response: CleanupOption[] | null;
    onContinue: () => void;
}

const ProposedCleanup: React.FC<ProposedCleanupProps> = ({
    json_response,
    onContinue,
}) => {
    const dispatch = useAppDispatch();
    const userId = jsCookie.get("user_id");
    const [searchParams] = useSearchParams();
    const threadId = searchParams.get("threadId");

    const [initialCleanupOptions] = useState(
        json_response?.map((option) => ({
            ...option,
            enabled: option.default === 1,
        })) || []
    );

    const [cleanupOptions, setCleanupOptions] = useState(
        json_response?.map((option) => ({
            ...option,
            enabled: option.default === 1,
        })) || []
    );

    const [open, setOpen] = useState<boolean>(true);
    const [isEditable, setIsEditable] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    // track expanded rows (accordion)
    const [expanded, setExpanded] = useState<number[]>([]);

    const handleToggle = (index: number) => {
        if (!isEditable) return;
        setCleanupOptions((prevOptions) =>
            prevOptions.map((option, i) =>
                i === index ? { ...option, enabled: !option.enabled } : option
            )
        );
    };

    const toggleExpand = (index: number) => {
        setExpanded((prev) =>
            prev.includes(index)
                ? prev.filter((i) => i !== index)
                : [...prev, index]
        );
    };

    const handleInputChange = (
        index: number,
        field: "name" | "description",
        value: string
    ) => {
        setCleanupOptions((prevOptions) =>
            prevOptions.map((option, i) =>
                i === index ? { ...option, [field]: value } : option
            )
        );
    };

    const handleContinue = async () => {
        setLoading(true);
        setIsEditable(false);
       

        if (
            JSON.stringify(cleanupOptions) ===
            JSON.stringify(initialCleanupOptions)
        ) {
            setLoading(false);
             onContinue();
            return;
        }

        try {
            const { data } = await dispatch(
                updateCleanup({
                    objectives: cleanupOptions,
                    userId: userId || "",
                    threadId: threadId || "",
                })
            ).unwrap();

            if (data) {
                 onContinue();
                setLoading(false);
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
        }
    };

    const handleEdit = () => {
        setIsEditable(true);
        setExpanded(cleanupOptions.map((_, idx) => idx)); // expand all accordions
    };

    return (
        <div
            className={`h-screen rounded-lg w-full overflow-hidden transition-all duration-300 ease-in-out ${open
                ? "flex-[0_0_100%] sm:flex-[0_0_75%] lg:flex-[0_0_460px] max-w-full lg:max-w-[460px]"
                : "flex-[0_0_100%] sm:flex-[0_0_215px] max-w-full sm:max-w-[215px]"
                }`}
        >
            <div className="flex flex-col h-full bg-opacity-50">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <img
                            src={BookIcon}
                            alt="Book Icon"
                            className="w-3 h-3 object-contain"
                        />
                        <h2 className="text-lg font-semibold">
                            Schema Validation & Evidence Readiness Audit
                        </h2>
                    </div>
                    <button
                        onClick={() => setOpen(!open)}
                        className="cursor-pointer rounded border border-[#DFE3E8] p-1 hover:bg-gray-100"
                        aria-label="Toggle Panel"
                    >
                        <img
                            src={SwitchIcon}
                            alt="Toggle"
                            className="w-3 h-3 object-contain"
                        />
                    </button>
                </div>

                {/* Main Content */}
                {open && (
                    <div className="h-[55vh] overflow-y-auto flex flex-col pn_scroller">
                        <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Proposed Clean-Up Plan
                        </h3>
                        <p className="text-xs text-gray-500 mb-2">
                            (You can toggle/edit each item before continuing)
                        </p>

                        {cleanupOptions.map((option, index) => (
                            <div key={index} className="p-1">
                                <div className="border border-[#D9D9D9] rounded-lg p-1">
                                    <div className="flex items-center gap-2 ">
                                        <span className="bg-[#F7F2FF] text-[#442873] py-1 px-2 rounded-lg">
                                            {index + 1}
                                        </span>

                                        {/* Editable name */}
                                        {isEditable ? (
                                            <textarea
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        index,
                                                        "name",
                                                        e.target.value
                                                    )
                                                }
                                                className="text-xs font-bold text-[#442873] border rounded px-1 flex-1 py-[4px]"
                                            >{option.name}</textarea>
                                        ) : (
                                            <span className="text-xs font-bold text-[#442873] truncate flex-1">
                                                {option.name}
                                            </span>
                                        )}

                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={option.enabled}
                                                onChange={() =>
                                                    handleToggle(index)
                                                }
                                                className={`${option.enabled
                                                    ? "bg-[#442873]"
                                                    : "bg-gray-300"
                                                    } relative inline-flex items-center h-3 w-6 rounded-full transition-colors`}
                                            >
                                                <span
                                                    className={`${option.enabled
                                                        ? "translate-x-3"
                                                        : "translate-x-0.5"
                                                        } inline-block w-2 h-2 transform bg-white rounded-full transition-transform`}
                                                />
                                            </Switch>
                                            <ChevronDownIcon
                                                onClick={() =>
                                                    toggleExpand(index)
                                                }
                                                className={`w-4 h-4  cursor-pointer transition-transform text-primary-900 ${expanded.includes(index)
                                                    ? "rotate-180"
                                                    : ""
                                                    }`}
                                            />
                                        </div>
                                    </div>

                                    {expanded.includes(index) && (
                                        <div className="mt-1 text-xs text-gray-600 rounded p-2">
                                            {isEditable ? (
                                                <textarea
                                                    value={option.description}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            "description",
                                                            e.target.value
                                                        )
                                                    }
                                                    className="w-full border rounded p-1 text-xs"
                                                />
                                            ) : (
                                                option.description
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Bottom Note */}
                {open && (
                    <div className="flex items-start gap-2 text-xs text-gray-600 mt-4 px-1">
                        <LightbulbIcon className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span>
                            All clean-ups are safe and reversible. You can adjust
                            FX rates, outlier rules, or dedupe logic.
                        </span>
                    </div>
                )}

                {/* Footer Actions */}
                {open && (
                    <div className="mt-4 pt-3 border-t border-[#DBDEEB] flex flex-col items-end">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleEdit}
                                className="cursor-pointer rounded-lg px-2 py-1 border border-secondary-500 hover:bg-foundationOrange-100 text-[#373D3F]"
                            >
                                Edit
                            </button>
                            <button
                                onClick={handleContinue}
                                disabled={loading}
                                className="cursor-pointer rounded-lg px-2 py-1 border bg-secondary-500 text-[#373D3F]"
                            >
                                {loading ? "Updating..." : "Accept & Continue"}
                            </button>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};

export default ProposedCleanup;
